import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { getCheckoutStatus } from "@/lib/api";

const MAX_ATTEMPTS = 10;
const POLL_INTERVAL = 2000;

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", data: null, error: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", error: "No session_id found in URL." });
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const data = await getCheckoutStatus(sessionId);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", data });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", data });
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ phase: "timeout", data });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ phase: "error", error: "Could not verify payment." });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <main data-testid="success-page" className="container-edit pt-20 md:pt-28 pb-32">
      <div className="max-w-2xl">
        {state.phase === "polling" && (
          <>
            <p className="text-xs uppercase tracking-[0.3em] text-brand-terracotta mb-5">Verifying</p>
            <h1 className="font-serif text-5xl md:text-6xl text-brand-forest leading-tight">
              Confirming your purchase…
            </h1>
            <p className="mt-8 text-brand-charcoal leading-relaxed">
              Please don't close this tab — this usually takes a few seconds.
            </p>
            <div className="mt-10 w-12 h-px bg-brand-terracotta animate-pulse" />
          </>
        )}

        {state.phase === "paid" && (
          <>
            <p className="text-xs uppercase tracking-[0.3em] text-brand-terracotta mb-5">Received</p>
            <h1 className="font-serif text-5xl md:text-6xl text-brand-forest leading-tight">
              Thank you. It's yours.
            </h1>
            <p className="mt-8 text-brand-charcoal leading-relaxed">
              Your copy of <strong>{state.data?.product_title}</strong> is ready.
              A confirmation email is on its way to you with the same secure
              download link.
            </p>
            <div className="mt-10 border border-brand-border bg-brand-surface p-8" data-testid="success-download-block">
              <p className="text-xs uppercase tracking-[0.3em] text-brand-muted mb-3">
                Your secure download
              </p>
              <p className="font-mono text-sm text-brand-forest break-all">
                Reference: {state.data?.download_token}
              </p>
              <button
                data-testid="success-download-btn"
                onClick={() => alert("In production this triggers your secure download. The reference above is your access token.")}
                className="mt-6 inline-flex items-center px-8 py-3 bg-brand-forest text-brand-bg text-sm tracking-wide hover:bg-brand-moss transition-colors rounded-sm"
              >
                Download your PDF
              </button>
            </div>
            <Link
              to="/shop"
              data-testid="success-back-shop"
              className="mt-10 inline-block text-sm uppercase tracking-[0.25em] text-brand-terracotta border-b border-brand-terracotta pb-1"
            >
              Return to the library →
            </Link>
          </>
        )}

        {state.phase === "expired" && (
          <>
            <h1 className="font-serif text-5xl text-brand-forest leading-tight">
              Session expired.
            </h1>
            <p className="mt-8 text-brand-charcoal leading-relaxed">
              The checkout session timed out. Nothing has been charged.
            </p>
            <Link
              to="/shop"
              data-testid="success-expired-back"
              className="mt-10 inline-flex items-center px-8 py-3 bg-brand-terracotta text-brand-bg text-sm tracking-wide hover:bg-brand-terracottaDark rounded-sm"
            >
              Back to the library
            </Link>
          </>
        )}

        {state.phase === "timeout" && (
          <>
            <h1 className="font-serif text-5xl text-brand-forest leading-tight">
              Still confirming…
            </h1>
            <p className="mt-8 text-brand-charcoal leading-relaxed">
              Your payment is taking a moment to confirm. Please check your inbox
              shortly, or refresh this page.
            </p>
          </>
        )}

        {state.phase === "error" && (
          <>
            <h1 className="font-serif text-5xl text-brand-forest leading-tight">
              Something went wrong.
            </h1>
            <p className="mt-8 text-brand-charcoal leading-relaxed">
              {state.error}
            </p>
            <Link to="/shop" className="mt-8 inline-block text-brand-terracotta underline">
              ← Back to the library
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
